// Les téléphones d'Arceau Game (D-167 ; maquettes T2 à T5), pour tous les jeux.
//  * Le téléphone d'une équipe : /j/<jeton>, ouvert par le QR de sa couleur sur la tablette. Sans compte, sans appli,
//    rien de nominatif (le nom de l'équipe et sa chasuble) ; valable le temps de la partie. Sa carte, son sable (Le
//    Sablier) ou son pion (Le Parcours), « STOP » ou « Fait ! », « Raté », la course des autres. Un deuxième téléphone sur
//    le même QR : « Regarder » ou « Prendre la main ». Hors réseau : rien ne compte, l'équipe va toucher la tablette.
//  * Le téléphone du coach : #/basket/jeu/coach/<partie>, avec son compte (le staff de l'équipe), sans code : la
//    télécommande. D-174 : toutes les équipes, chacune avec ses boutons (« STOP », « Raté » ; « Lancer le dé »,
//    « Fait ! », les tirs), son arbitrage (invalider, sanctionner, changer une carte) ; pause, annuler, arrêter ; et les QR
//    des équipes sur ce téléphone même (une partie sans tablette), ou sur l'écran de la partie.
// La base fait foi : un geste n'existe qu'une fois écrit (à l'heure de la base) ; l'écran relit la partie publiée par la
// tablette (le temps réel prévient plus vite quand il est là ; sinon on relit chaque seconde).
import './game.css';
import './game2.css';
import { fmtGameTime } from '../domain/game';
import { cardTitle } from '../domain/game-cards';
import { ClockSync, deviceId, readLiveView, viewSand, viewTime, type LiveTeam, type LiveView } from '../domain/game-live';
import { liveTokens, openPinger, phoneAct, phoneJoin, phoneView, type PhoneJoin, type PhoneKey, type Pinger } from '../lib/game-live-api';
import { cardHtml, esc, hourglass, jersey, qrSvg, type Kit } from './game-art';
import { ICON, teamLink } from './game-live-run';
import { BIB_HEX, BIB_INK, type Bib } from '../domain/groups';
import { BIB_LETTER } from '../domain/parcours';
import type { Ctx } from './context';

const IC = {
  pause: `<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>`,
  play: `<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 4v16l13-8z"/></svg>`,
  ban: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M5.6 5.6l12.8 12.8"/></svg>`,
  flag: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 21V4M5 4h11l-2 4 2 4H5"/></svg>`,
  undo: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 14L4 9l5-5"/><path d="M4 9h10a6 6 0 0 1 0 12h-3"/></svg>`,
  check: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12l5 5L20 7"/></svg>`,
};
const OFF_MS = 5_000;
const kitOf = (t: LiveTeam): Kit => ({ color: t.color, ink: t.ink, letter: t.letter });
const BIB_WORD: Record<string, string> = { red: 'rouge', blue: 'bleue', yellow: 'jaune', green: 'verte', orange: 'orange', purple: 'violette' };
/** « des Bleues » */
const ofName = (name: string) => (name.startsWith('Les ') ? `des ${name.slice(4)}` : `de ${name}`);
const clockTxt = (ms: number) => fmtGameTime(Math.ceil(ms / 1000) * 1000);

/** L'identifiant de ce téléphone pour la partie : tiré au hasard, gardé le temps de l'onglet (rien de personnel). */
function myDevice(): string {
  const K = 'arceau.jeu.appareil';
  try { const v = sessionStorage.getItem(K); if (v && /^[a-z0-9]{24}$/.test(v)) return v; const d = deviceId(); sessionStorage.setItem(K, d); return d; }
  catch { return deviceId(); }
}

interface PhoneState {
  key: PhoneKey;
  device: string;
  clock: ClockSync;
  info: PhoneJoin | null;
  view: LiveView | null;
  me: 'play' | 'watch';
  /** Le choix fait sur l'écran « déjà un téléphone » (T5). */
  chose: boolean;
  lastOk: number;
  lastView: number;
  closed: boolean;
  pinger: Pinger | null;
  busy: boolean;
  msg: string | null;
  open: string | null;
  armed: string | null;
  askJoker: boolean;
  /** La télécommande : les QR des équipes affichés sur ce téléphone ; leurs jetons ; l'équipe à qui l'on demande le joker. */
  qr: boolean;
  tokens: { seat: string; token: string; name: string; bib: string }[] | null;
  askSeat: string | null;
}

// -----------------------------------------------------------------------------------------------------------------
// Le moteur commun des deux téléphones : rejoindre, relire, agir
// -----------------------------------------------------------------------------------------------------------------
function phoneLoop(el: HTMLElement, key: PhoneKey, draw: (st: PhoneState) => string, wire: (st: PhoneState, act: (kind: string, payload?: Record<string, unknown>) => Promise<void>, redraw: () => void) => void): () => void {
  const st: PhoneState = { key, device: myDevice(), clock: new ClockSync(), info: null, view: null, me: 'watch', chose: false, lastOk: 0, lastView: 0, closed: false, pinger: null, busy: false, msg: null, open: null, armed: null, askJoker: false, qr: false, tokens: null, askSeat: null };
  let timer = 0, tickT = 0, stopped = false;
  // Jamais de nouvel écran pendant qu'un doigt appuie : le geste passerait à côté ; on redessine au relâcher.
  let pressing = false, owed = false;
  const redraw = () => { if (stopped) return; if (pressing) { owed = true; return; } owed = false; el.innerHTML = draw(st); wire(st, act, redraw); };
  el.addEventListener('pointerdown', () => { pressing = true; });
  for (const ev of ['pointerup', 'pointercancel']) el.addEventListener(ev, () => { setTimeout(() => { pressing = false; if (owed) redraw(); }, 60); });
  const take = (j: PhoneJoin | null) => {
    if (!j) { st.closed = true; return; }
    st.info = { ...st.info, ...j } as PhoneJoin;
    st.me = j.me;
    const v = readLiveView(j.state);
    if (v && (!st.view || v.seq >= st.view.seq || !v.started)) st.view = v;
    if (j.status === 'closed') st.closed = true;
    st.lastOk = Date.now(); st.lastView = st.clock.now();
  };
  const poll = async () => {
    try { take(await phoneView(key, st.device, st.clock)); } catch { /* hors réseau : l'écran le dit */ }
    redraw();
  };
  async function act(kind: string, payload: Record<string, unknown> = {}) {
    if (st.busy) return;
    st.busy = true; st.msg = null; redraw();
    try {
      await phoneAct(key, st.device, kind as never, payload, st.clock);
      st.lastOk = Date.now();
      st.pinger?.send('act', {});
      if ('vibrate' in navigator) { try { navigator.vibrate(30); } catch { /* rien */ } }
    } catch (e) {
      const code = (e as { code?: string }).code;
      st.msg = code === '42501' ? 'Un autre téléphone a la main : celui-ci regarde.' : code ? 'Pas maintenant : la partie n’est pas en cours.'
        : 'Pas envoyé (réseau) : allez le toucher sur l’écran de la partie.';
      if (code === '42501') st.me = 'watch';
    }
    st.busy = false;
    void poll();
  }
  const schedule = () => { clearInterval(timer); timer = window.setInterval(() => void poll(), st.pinger?.live ? 4000 : 1000); };
  void (async () => {
    try { take(await phoneJoin(key, st.device, st.clock)); }
    catch (e) { st.closed = !(e as { code?: string }).code ? false : true; st.msg = (e as { code?: string }).code === '42501' ? 'refus' : null; }
    st.chose = st.me === 'play';
    redraw();
    if (st.info?.channel) {
      st.pinger = openPinger(st.info.channel, (ev, payload) => {
        if (ev !== 'state') return;
        if (payload.closed) { void poll(); return; }
        const v = readLiveView(payload.view);
        if (v && (!st.view || v.seq > st.view.seq)) { st.view = v; st.lastOk = Date.now(); redraw(); }
      }, () => schedule());
    }
    schedule();
  })();
  // Le sable coule entre deux nouvelles : on redessine chaque seconde.
  tickT = window.setInterval(() => { if (!st.busy) redraw(); }, 1000);
  const onVis = () => { if (document.visibilityState === 'visible') void poll(); };
  document.addEventListener('visibilitychange', onVis);
  return () => { stopped = true; clearInterval(timer); clearInterval(tickT); st.pinger?.close(); document.removeEventListener('visibilitychange', onVis); };
}

const offline = (st: PhoneState) => !!st.lastOk && Date.now() - st.lastOk > OFF_MS;
const nowT = (st: PhoneState) => (st.view ? viewTime(st.view, st.clock.now()) : 0);
const head = (st: PhoneState, chip: string, title?: string) => {
  const i = st.info;
  const logo = i?.logo ? `<img src="${esc(i.logo)}" alt="">` : `<span class="lg" aria-hidden="true"></span>`;
  const game = i?.kind === 'sablier' ? 'Le Sablier' : 'Le Parcours';
  return `<div class="tel-h">${logo}<span><b>${esc(title ?? game)}</b><small>${esc(title ? `${game} · ${i?.team ?? ''}` : [i?.club, i?.team].filter(Boolean).join(' · '))}</small></span>${chip}</div>`;
};
const chipOf = (st: PhoneState) => (offline(st) ? `<span class="tel-chip off"><i></i>Hors réseau</span>` : st.me === 'watch' ? `<span class="tel-chip watch"><i></i>Regarde</span>` : `<span class="tel-chip"><i></i>Relié</span>`);

// -----------------------------------------------------------------------------------------------------------------
// Le téléphone d'une équipe (sans compte)
// -----------------------------------------------------------------------------------------------------------------
export function renderTeamPhone(root: HTMLElement, token: string): void {
  document.title = 'Arceau Game';
  root.innerHTML = '';
  const el = document.createElement('main');
  el.className = 'tel tel-page';
  root.append(el);
  phoneLoop(el, { token }, drawTeam, wireTeam);
}

function myTeam(st: PhoneState): LiveTeam | null {
  return st.view?.teams.find(t => t.seat === st.info?.seat) ?? null;
}
function race(st: PhoneState, me: string): string {
  const v = st.view!;
  const t = nowT(st);
  if (v.kind === 'sablier') {
    const best = Math.max(1, ...v.teams.map(x => viewSand(x, t)), v.reserve * 1000);
    return `<div class="tel-race"><p>La course</p>${v.teams.map(x => { const s = viewSand(x, t); return `<div class="${x.seat === me ? 'me' : ''} ${s < 60_000 ? 'low' : ''}" style="--c:${x.color}">${jersey(kitOf(x), 22)}<span>${esc(x.name.replace(/^Les /, ''))}${x.seat === me ? ' · vous' : ''}</span><span class="bar"><i style="width:${Math.round(s / best * 100)}%"></i></span><em>${clockTxt(s)}</em></div>`; }).join('')}</div>`;
  }
  return `<div class="tel-race"><p>La course</p>${v.teams.map(x => `<div class="${x.seat === me ? 'me' : ''}" style="--c:${x.color}">${jersey(kitOf(x), 22)}<span>${esc(x.name.replace(/^Les /, ''))}${x.seat === me ? ' · vous' : ''}</span><span class="bar"><i style="width:${Math.round(x.at / Math.max(1, v.n) * 100)}%"></i></span><em>${x.at}</em></div>`).join('')}</div>`;
}

function drawTeam(st: PhoneState): string {
  const i = st.info;
  if (!i && !st.closed) return `<div class="tel-wait"><b>Un instant…</b><p>On rejoint la partie.</p></div>`;
  if (st.closed || !i) {
    return `${i ? head(st, '') : ''}<div class="tel-wait"><b>La partie est finie</b><p>Ce lien ne vaut que le temps d’une partie : il s’est éteint. Pour la prochaine, scannez le nouveau QR sur l’écran de la partie.</p></div>
      <p class="tel-foot">Sans compte · rien n’est gardé sur ce téléphone.</p>`;
  }
  const v = st.view;
  const t = myTeam(st);
  const name = i.name;
  const kit: Kit = t ? kitOf(t) : { color: '#9AA1AA', ink: '#FFFFFF', letter: '?' };
  const band = (n: string) => `<div class="tel-team" style="--c:${kit.color};--ci:${kit.ink}">${jersey(kit, 46)}<span><b>${esc(name)}</b><small>votre équipe${i.bib ? ` · chasuble ${BIB_WORD[i.bib] ?? ''}` : ''}</small></span><span class="n">${n}</span></div>`;
  // Deux téléphones sur le même QR (T5).
  if (st.me === 'watch' && !st.chose) {
    const ago = i.since ? Math.max(0, Math.round((st.clock.now() - i.since) / 60_000)) : 0;
    return `${head(st, '')}
      <div class="tel-dup">${jersey(kit, 96)}<h2>${esc(name)} ${name.startsWith('Les ') ? 'ont' : 'a'} déjà un téléphone</h2>
        <p>Un seul téléphone joue par équipe : celui qui a scanné le premier, il y a ${ago ? `${ago} min` : 'moins d’une minute'}.</p></div>
      <button type="button" class="tel-choice p" data-watch><span class="i">${ICON.eye}</span><span><strong>Regarder la partie</strong><small>${i.kind === 'sablier' ? `Le sable ${esc(ofName(name))}` : `Le pion ${esc(ofName(name))}`}, leur carte et la course, sans les boutons.</small></span></button>
      <button type="button" class="tel-choice g" data-take><span class="i">${ICON.hand}</span><span><strong>Prendre la main</strong><small>Si le premier est perdu ou à plat. Il passe en spectateur ; l’écran de la partie l’affiche.</small></span></button>
      <div class="tel-sep"></div>
      <div class="tel-note"><span class="i">${ICON.qr}</span><span><b>Pas votre couleur ?</b> Scannez la vôtre sur l’écran de la partie.</span></div>
      <div class="tel-note"><span class="i">${ICON.lock}</span><span><b>Le QR du coach</b> ne s’ouvre qu’avec le compte d’un coach de l’équipe : ici, il demanderait de se connecter.</span></div>
      <p class="tel-foot">Sans compte · rien n’est gardé sur ce téléphone · le lien s’éteint à la fin de la partie.</p>`;
  }
  if (!v || !v.started || !t) {
    return `${head(st, chipOf(st))}${band('')}
      <div class="tel-wait"><b>Bientôt le coup d’envoi</b><p>Le coach lance la partie sur l’écran de la partie. ${i.kind === 'sablier' ? 'Votre carte et votre sable s’afficheront ici.' : 'Votre pion et vos cartes s’afficheront ici.'}</p></div>
      <p class="tel-foot"><b>Posez ce téléphone au plot ${esc(ofName(name))}</b>. Sans compte · rien n’est gardé ici.</p>`;
  }
  const off = offline(st);
  const tt = nowT(st);
  const msg = st.msg ? `<div class="tel-banner watch"><span>${ICON.phone}</span><span><b>${esc(st.msg)}</b></span></div>` : '';
  const watchBar = st.me === 'watch' ? `<div class="tel-banner watch"><span>${ICON.eye}</span><span><b>Ce téléphone regarde</b><span>Un autre joue pour l’équipe. <button type="button" class="tel-link" data-take style="padding:0">Prendre la main</button></span></span></div>` : '';
  if (v.over) return drawOver(st, t, band);
  if (v.kind === 'sablier') return drawSablierTeam(st, v, t, tt, off, band, msg + watchBar);
  return drawParcoursTeam(st, v, t, off, band, msg + watchBar);
}

function drawSablierTeam(st: PhoneState, v: LiveView, t: LiveTeam, tt: number, off: boolean, band: (n: string) => string, top: string): string {
  const sand = viewSand(t, tt);
  const read = t.flowFrom !== null && tt < t.flowFrom;
  const flowing = !v.paused && t.flowFrom !== null && !read && t.phase === 'defi';
  const name = st.info!.name;
  const glass = hourglass(t.color, sand / Math.max(1, v.reserve * 1000), { flow: flowing && !off, w: off ? 54 : 66, cls: off ? 'gray' : '', label: `Votre sable` });
  if (off) {
    const ago = Math.round((Date.now() - st.lastOk) / 1000);
    const last = viewTime(v, st.lastView);
    return `<div class="tel-off" style="display:contents">${head(st, chipOf(st))}
      <div class="tel-banner">${ICON.wifioff}<span><b>Plus de réseau depuis ${ago} s</b><span>Votre sable coule toujours, sur l’écran de la partie.</span></span></div>
      ${band(`carte<br><b>${t.ok + t.ko + 1}</b>`)}
      <div class="tel-time">${glass}<span><b>≈ ${clockTxt(sand)}</b><small><em>dernière nouvelle à ${fmtGameTime(last)} · sans doute moins</em></small></span></div>
      ${t.card ? `<div class="tel-card">${cardHtml(t.card, 132, { game: 'sablier' })}</div>` : ''}
      <div class="tel-dis"><b>STOP sur l’écran de la partie</b><span>Sans réseau, un STOP touché ici ne compterait pas : allez le toucher sur l’écran de la partie, sur la ligne ${esc(ofName(name))}.</span></div>
      <ol class="tel-list"><li><b>Rien n’est perdu</b> : la partie vit dans la base d’Arceau, pas sur ce téléphone.</li>
        <li><b>Le réseau revient</b> : l’écran se recale tout seul, sans rien renvoyer.</li>
        <li><b>Plus de batterie ?</b> Rescannez le QR ${esc(ofName(name))} avec un autre téléphone.</li></ol>
      <button type="button" class="tel-link" data-retry>Réessayer maintenant</button></div>`;
  }
  const sub = v.paused ? 'en pause' : t.phase === 'out' ? 'plus de sable' : read ? `le sable attend <em>· ${Math.max(1, Math.ceil((t.flowFrom! - tt) / 1000))} s pour lire la carte</em>`
    : t.phase === 'wait' ? `arrêté <em>· ${esc(t.note ?? '')}</em>` : `le sable coule <em>· en défi depuis ${fmtGameTime(tt - (t.flowFrom ?? tt))}</em>`;
  const can = st.me === 'play' && !st.busy && !v.paused && t.phase === 'defi' && !read;
  const hasCancel = t.jokers.includes('cancel');
  const btns = t.phase === 'defi' ? `<div class="tel-btns"><button type="button" class="gm-btn ok" data-act="stop" ${can ? '' : 'disabled'}>${ICON.stop}<span>Stop<small>c’est fait</small></span></button>
      <button type="button" class="gm-btn no" data-act="fail" ${can ? '' : 'disabled'}>Raté</button></div>
      ${t.jokers.includes('change') ? `<button type="button" class="gm-btn joker" data-act="joker" ${can ? '' : 'disabled'}>Joker : change de défi</button>` : ''}
      ${hasCancel ? `<p class="tel-foot">Joker « annule un raté » en main : il joue tout seul au prochain raté.</p>` : ''}`
    : `<div class="tel-dis"><b>${t.phase === 'out' ? 'À zéro' : v.mode === 'same' ? 'On attend les autres' : 'La carte suivante arrive'}</b><span>${t.phase === 'out' ? 'Le dernier grain est tombé.' : 'Elle se retourne sur l’écran de la partie, puis ici.'}</span></div>`;
  return `${head(st, chipOf(st))}${top}
    ${band(`<b>${t.ok}</b><br>défi${t.ok > 1 ? 's' : ''}<br>réussi${t.ok > 1 ? 's' : ''}`)}
    <div class="tel-time ${sand < 60_000 ? 'low' : ''}">${glass}<span><b>${clockTxt(sand)}</b><small>${sub}</small></span></div>
    ${t.card && t.phase === 'defi' ? `<div class="tel-card">${cardHtml(t.card, 188, { game: 'sablier' })}</div>` : ''}
    ${btns}
    ${race(st, t.seat)}
    <p class="tel-foot"><b>Posez ce téléphone au plot ${esc(ofName(st.info!.name))}</b> : on revient le toucher pour STOP. Sans compte · rien n’est gardé ici.</p>`;
}

function drawParcoursTeam(st: PhoneState, v: LiveView, t: LiveTeam, off: boolean, band: (n: string) => string, top: string): string {
  const name = st.info!.name;
  if (off) {
    const ago = Math.round((Date.now() - st.lastOk) / 1000);
    return `<div class="tel-off" style="display:contents">${head(st, chipOf(st))}
      <div class="tel-banner">${ICON.wifioff}<span><b>Plus de réseau depuis ${ago} s</b><span>La partie continue, sur l’écran de la partie.</span></span></div>
      ${band(`case<br><b>${t.at}</b>`)}
      <div class="tel-dis"><b>Sur l’écran de la partie</b><span>Sans réseau, un geste touché ici ne compterait pas : allez le toucher sur l’écran de la partie, sur la ligne ${esc(ofName(name))}.</span></div>
      <ol class="tel-list"><li><b>Rien n’est perdu</b> : la partie vit dans la base d’Arceau, pas sur ce téléphone.</li>
        <li><b>Le réseau revient</b> : l’écran se recale tout seul, sans rien renvoyer.</li>
        <li><b>Plus de batterie ?</b> Rescannez le QR ${esc(ofName(name))} avec un autre téléphone.</li></ol>
      <button type="button" class="tel-link" data-retry>Réessayer maintenant</button></div>`;
  }
  const can = st.me === 'play' && !st.busy && !v.paused;
  let body = '';
  if (t.phase === 'roll' || t.phase === '') {
    body = t.skip ? `<div class="tel-dis"><b>Passe son tour</b><span>Chaque autre équipe lance le dé une fois.</span></div>`
      : `<div class="tel-btns one"><button type="button" class="gm-btn team" style="--c:${t.color};--ci:${t.ink}" data-act="roll" ${can && t.canRoll ? '' : 'disabled'}>Lancer le dé</button></div>
         <p class="tel-foot">Le dé roule sur l’écran de la partie, le pion avance, la carte se retourne.</p>`;
  } else if (t.phase === 'defi' && t.card) {
    body = st.askJoker ? `<p class="tel-foot">Votre joker « annule un raté » : on l’utilise ?</p><div class="tel-btns"><button type="button" class="gm-btn ok" data-act="fail" data-joker="cancel">Joker</button><button type="button" class="gm-btn no" data-act="fail">Non</button></div>`
      : `<div class="tel-card">${cardHtml(t.card, 188)}</div>
      <div class="tel-btns"><button type="button" class="gm-btn ok" data-act="done" ${can ? '' : 'disabled'}>${IC.check}<span>Fait !<small>c’est réussi</small></span></button>
        <button type="button" class="gm-btn no" data-act="fail" ${can ? '' : 'disabled'}>Raté</button></div>
      ${t.jokers.includes('change') ? `<button type="button" class="gm-btn joker" data-act="joker" ${can ? '' : 'disabled'}>Joker : change de défi</button>` : ''}`;
  } else if (t.phase === 'bonus') {
    body = `<div class="tel-dis"><b>Case d’action · ${t.shots.filter(Boolean).length} sur ${t.shots.length}</b><span>Après chaque tir, touchez « Marqué » ou « Raté ».</span></div>
      <div class="tel-btns"><button type="button" class="gm-btn ok" data-act="shot" data-made="1" ${can ? '' : 'disabled'}>Marqué</button><button type="button" class="gm-btn no" data-act="shot" data-made="0" ${can ? '' : 'disabled'}>Raté</button></div>`;
  } else if (t.phase === 'done') body = `<div class="tel-dis"><b>Arrivée !</b><span>Sous le panier.</span></div>`;
  return `${head(st, chipOf(st))}${top}
    ${band(`<b>${t.ok}</b><br>défi${t.ok > 1 ? 's' : ''}<br>réussi${t.ok > 1 ? 's' : ''}`)}
    <div class="tel-pawn">${jersey(kitOf(t), 56)}<span><b>Case ${t.at}</b><small>sur ${v.n}${t.note ? ` · ${esc(t.note)}` : ''}</small></span></div>
    ${body}
    ${race(st, t.seat)}
    <p class="tel-foot"><b>Posez ce téléphone au plot ${esc(ofName(name))}</b>. Sans compte · rien n’est gardé ici.</p>`;
}

function drawOver(st: PhoneState, t: LiveTeam, band: (n: string) => string): string {
  const v = st.view!;
  const r = v.ranks.find(x => x.seat === t.seat)?.rank ?? null;
  const first = v.teams.find(x => x.seat === v.ranks.find(y => y.rank === 1)?.seat);
  const tt = v.t;
  return `${head(st, chipOf(st))}${band(r ? `place<br><b>${r}</b>` : '')}
    <div class="tel-wait"><b>${r === 1 ? 'Gagné !' : 'Fin de la partie'}</b>
      <p>${first ? `${esc(first.name)} ${v.kind === 'sablier' ? `${first.name.startsWith('Les ') ? 'gardent' : 'garde'} le plus de sable` : `${first.name.startsWith('Les ') ? 'arrivent' : 'arrive'} en tête`}.` : ''} Le bilan est sur l’écran de la partie.</p></div>
    ${v.kind === 'sablier' ? `<div class="tel-time">${hourglass(t.color, viewSand(t, tt) / Math.max(1, v.reserve * 1000), { w: 54 })}<span><b>${clockTxt(viewSand(t, tt))}</b><small>votre sable à la fin</small></span></div>` : ''}
    ${race(st, t.seat)}
    <p class="tel-foot">Sans compte · rien n’est gardé ici · le lien s’éteint quand le coach ferme la partie.</p>`;
}

function wireTeam(st: PhoneState, act: (kind: string, payload?: Record<string, unknown>) => Promise<void>, redraw: () => void): void {
  const el = document.querySelector<HTMLElement>('.tel')!;
  el.onclick = async e => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('button');
    if (!b || b.disabled) return;
    if (b.hasAttribute('data-watch')) { st.chose = true; redraw(); return; }
    if (b.hasAttribute('data-take')) {
      try { const j = await phoneJoin(st.key, st.device, st.clock, true); if (j) { st.info = { ...st.info!, ...j }; st.me = j.me; } st.chose = true; st.msg = null; } catch { st.msg = 'Pas maintenant (réseau) : réessayez.'; }
      redraw(); return;
    }
    if (b.hasAttribute('data-retry')) { st.lastOk = Date.now(); redraw(); return; }
    const kind = b.dataset.act;
    if (!kind) return;
    const t = myTeam(st);
    if (kind === 'fail' && st.view?.kind === 'parcours' && t?.jokers.includes('cancel') && !st.askJoker && !b.dataset.joker) { st.askJoker = true; redraw(); return; }
    st.askJoker = false;
    const payload: Record<string, unknown> = {};
    if (kind === 'shot') payload.made = b.dataset.made === '1';
    if (kind === 'joker') payload.joker = 'change';
    if (kind === 'fail' && (b.dataset.joker === 'cancel' || (st.view?.kind === 'sablier' && t?.jokers.includes('cancel')))) payload.joker = 'cancel';
    await act(kind, payload);
  };
}

// -----------------------------------------------------------------------------------------------------------------
// Le téléphone du coach : la télécommande (T3), avec son compte
// -----------------------------------------------------------------------------------------------------------------
export function renderCoachPhone(root: HTMLElement, ctx: Ctx, liveId: string): void {
  root.innerHTML = '';
  const el = document.createElement('div');
  el.className = 'tel';
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-label', 'Télécommande');
  root.append(el);
  void ctx;
  const stop = phoneLoop(el, { live: liveId }, drawCoach, wireCoach);
  const off = () => { if (!document.body.contains(el)) { stop(); window.removeEventListener('hashchange', off); } };
  window.addEventListener('hashchange', off);
}

function drawCoach(st: PhoneState): string {
  const i = st.info;
  if (!i && !st.closed) return st.msg === 'refus'
    ? `<div class="tel-wait"><b>Réservé au staff de l’équipe</b><p>La télécommande s’ouvre avec le compte d’un coach de l’équipe.</p><a class="tel-link" href="#/basket/drills">Retour</a></div>`
    : `<div class="tel-wait"><b>Un instant…</b><p>On rejoint la partie.</p></div>`;
  if (st.closed || !i) return `<div class="tel-wait"><b>La partie est finie</b><p>La télécommande s’est éteinte avec elle.</p><a class="tel-link" href="#/basket/drills">Retour à Drills et jeux</a></div>`;
  const v = st.view;
  const who = i.holder_name ?? 'Coach';
  const chip = st.me === 'play' ? `<span class="tel-chip coach">${ICON.unlock}${esc(who)} · coach</span>` : `<span class="tel-chip watch"><i></i>Regarde</span>`;
  const take = st.me === 'watch' ? `<button type="button" class="tel-choice g" data-take><span class="i">${ICON.hand}</span><span><strong>Prendre la main</strong><small>Un autre téléphone de coach tient la télécommande.</small></span></button>` : '';
  if (st.qr) return drawCoachQr(st, chip, !!v?.started && !v.over && st.me === 'play');
  const qrBtn = `<button type="button" class="tel-choice g" data-qr><span class="i">${ICON.qr}</span><span><strong>Les QR des équipes</strong><small>Sur ce téléphone : chaque équipe vise sa couleur. Pour une partie sans tablette.</small></span></button>`;
  if (!v || !v.started) {
    return `${head(st, chip, 'Télécommande')}
      <div class="tel-wait"><b>Avant le coup d’envoi</b><p>Les équipes qui ont un téléphone scannent leur couleur, sur l’écran de la partie ou ici. Dès le coup d’envoi : STOP, Raté et l’arbitrage de chaque équipe, la pause, sans code.</p></div>
      ${qrBtn}${take}`;
  }
  const tt = nowT(st);
  const sab = v.kind === 'sablier';
  const can = st.me === 'play' && !st.busy;
  const play = can && !v.over && !v.paused;
  const dis = (on: boolean) => (on ? '' : 'disabled');
  /** Ce que ferait le téléphone de l'équipe, pour elle (D-174). */
  const acts = (t: LiveTeam) => {
    const idle = (txt: string) => `<span class="tel-idle">${esc(txt)}</span>`;
    if (v.over) return idle(t.phase === 'out' ? 'À zéro' : 'Partie finie');
    if (sab) {
      if (t.phase !== 'defi') return idle(t.phase === 'out' ? 'À zéro' : v.mode === 'same' ? 'Fait · attend les autres' : 'Fait · la carte suivante arrive');
      const read = t.flowFrom !== null && tt < t.flowFrom;
      return `<button type="button" class="gm-btn ok" data-c="stop" data-seat="${t.seat}" ${dis(play && !read)}>${ICON.stop}Stop</button>
        <button type="button" class="gm-btn no" data-c="fail" data-seat="${t.seat}" ${dis(play && !read)}>Raté</button>`;
    }
    if (t.phase === 'defi' && t.card) {
      if (st.askSeat === t.seat) return `<button type="button" class="gm-btn ok" data-c="fail" data-seat="${t.seat}" data-joker="cancel" ${dis(play)}>Joker</button>
        <button type="button" class="gm-btn no" data-c="fail" data-seat="${t.seat}" data-nojoker ${dis(play)}>Raté</button>`;
      return `<button type="button" class="gm-btn ok" data-c="done" data-seat="${t.seat}" ${dis(play)}>${IC.check}Fait !</button>
        <button type="button" class="gm-btn no" data-c="fail" data-seat="${t.seat}" ${dis(play)}>Raté</button>`;
    }
    if (t.phase === 'bonus') return `<button type="button" class="gm-btn ok" data-c="shot" data-seat="${t.seat}" data-made="1" ${dis(play)}>Marqué</button>
      <button type="button" class="gm-btn no" data-c="shot" data-seat="${t.seat}" data-made="0" ${dis(play)}>Raté</button>`;
    if (t.phase === 'done') return idle('Arrivée !');
    if (t.skip) return idle('Passe son tour');
    return `<button type="button" class="gm-btn team wide" style="--c:${t.color};--ci:${t.ink}" data-c="roll" data-seat="${t.seat}" ${dis(play && t.canRoll)}>Lancer le dé</button>`;
  };
  const row = (t: LiveTeam) => {
    const sand = viewSand(t, tt);
    const open = st.open === t.seat;
    const stl = t.phase === 'out' ? 'À zéro' : t.card ? `${t.phase === 'defi' ? 'En défi' : t.note ?? 'Fait'} · ${cardTitle(t.card)}` : t.note ?? (sab ? 'En attente' : `case ${t.at}`);
    return `<div class="tel-row ${sab && sand < 60_000 ? 'low' : ''} ${open ? 'open' : ''}" style="--c:${t.color}" data-row="${t.seat}">${jersey(kitOf(t), 34)}
      <span><strong>${esc(t.name)}</strong><small>${esc(stl)}</small></span><span class="t"><b>${sab ? clockTxt(sand) : t.at}</b><i>${sab ? '' : 'case'}</i></span>
      <div class="tel-acts">${acts(t)}<button type="button" class="arb" data-open="${t.seat}" aria-expanded="${String(open)}" aria-label="Arbitrer : ${esc(t.name)}">${IC.flag}Arbitrer</button></div></div>`;
  };
  const panel = (t: LiveTeam) => {
    const last = t.last;
    const lastTxt = sab ? (last ? `Dernier STOP ${esc(ofName(t.name))} : <b>« ${esc(last.title)} », à ${fmtGameTime(last.at)}</b>, en ${fmtGameTime(last.ms)}.` : `Pas encore de STOP ${esc(ofName(t.name))}.`)
      : `${esc(t.name)} : case ${t.at}${t.note ? ` · ${esc(t.note)}` : ''}.`;
    const sanctions = sab ? [['15', '−15 s'], ['30', '−30 s']] : [['back1', 'Recule de 1'], ['back2', 'de 2'], ['back3', 'de 3'], ['skip', 'Passe un tour']];
    return `<div class="tel-panel"><p class="h">${lastTxt}</p>
      <button type="button" class="gm-crow main" data-c="invalidate" data-seat="${t.seat}" ${can && (sab ? !!last : t.ok > 0) ? '' : 'disabled'}><span class="ico">${IC.ban}</span><span><strong>${sab ? 'Invalider ce STOP' : 'Invalider le dernier défi'}</strong><small>${sab ? 'La carte revient, le sable recoule depuis maintenant.' : 'Le pion revient, la carte aussi.'}</small></span></button>
      <div class="gm-crow plain"><span class="ico">${IC.flag}</span><span><strong>Sanction</strong><span class="chips">${sanctions.map(([k, l]) => `<button type="button" data-c="sanction" data-seat="${t.seat}" data-v="${k}" class="${st.armed === `${t.seat}:${k}` ? 'armed' : ''}" ${can ? '' : 'disabled'}>${l}</button>`).join('')}</span></span></div>
      ${sab ? `<button type="button" class="gm-crow" data-c="change" data-seat="${t.seat}" ${can && t.phase === 'defi' ? '' : 'disabled'}><span class="ico">${ICON.swap}</span><span><strong>Changer leur carte</strong><small>Carte injouable (matériel, blessure) : une autre, sans pénalité.</small></span></button>` : ''}</div>`;
  };
  const rows = v.teams.map(t => `${row(t)}${st.open === t.seat ? panel(t) : ''}`).join('');
  const msg = st.msg ? `<div class="tel-banner watch"><span>${ICON.phone}</span><span><b>${esc(st.msg)}</b></span></div>` : '';
  return `${head(st, chip, 'Télécommande')}${msg}
    ${take}
    <div class="tel-clock"><span><b>${fmtGameTime(v.over ? v.t : tt)}</b><small>${v.over ? 'partie terminée' : v.paused ? 'en pause' : sab ? `temps de jeu · ${Math.round(v.reserve / 60)} min de réserve au départ` : `temps de jeu · ${v.n} cases`}</small></span>
      ${v.over ? '' : `<button type="button" class="pz" data-c="pause" ${can ? '' : 'disabled'}>${v.paused ? `${IC.play}Reprendre` : `${IC.pause}Pause`}</button>`}</div>
    <div class="tel-rows">${rows}</div>
    <div class="tel-tools"><button type="button" data-c="undo" ${can && v.last && !v.over ? '' : 'disabled'}>${IC.undo}Annuler la dernière action</button>
      <button type="button" data-qr>${ICON.qr}Les QR des équipes</button>
      <button type="button" class="stop ${st.armed === 'end' ? 'armed' : ''}" data-c="end" ${can && !v.over ? '' : 'disabled'}>${ICON.stop}${st.armed === 'end' ? 'Arrêter ? Touche encore' : 'Arrêter la partie'}</button></div>
    <p class="tel-foot">Connecté avec ton compte de coach${i.team ? ` des ${esc(i.team)}` : ''} : <b>pas de code</b>. L’écran de la partie affiche « Télécommande de ${esc(who)} » ou « Arbitré par ${esc(who)} » à chaque geste.</p>
    <a class="tel-link" href="#/basket/drills">Fermer la télécommande</a>`;
}

/** Les QR des équipes sur le téléphone du coach (D-174) : une partie sans tablette, ou la tablette trop loin. */
function drawCoachQr(st: PhoneState, chip: string, canTab: boolean): string {
  const list = st.tokens;
  const cards = list === null ? `<div class="tel-wait"><b>Un instant…</b><p>On cherche les QR de la partie.</p></div>`
    : !list.length ? `<div class="tel-wait"><b>Pas de QR</b><p>Cette partie se joue sans téléphones d’équipe : tout se touche sur l’écran de la partie, ou ici.</p></div>`
    : `<div class="tel-qrs">${list.map(x => {
        const bib = (x.bib in BIB_HEX ? x.bib : 'red') as Bib;
        const k: Kit = { color: BIB_HEX[bib], ink: BIB_INK[bib], letter: BIB_LETTER[bib] };
        return `<div class="tel-qr" style="--c:${k.color};--ci:${k.ink}"><div class="band">${jersey(k, 26)}<b>${esc(x.name)}</b></div>
          <div class="qr">${qrSvg(teamLink(x.token), `QR ${ofName(x.name)}`)}</div></div>`;
      }).join('')}</div>`;
  return `${head(st, chip, 'Les QR des équipes')}
    <p class="tel-lead">Chaque équipe ouvre l’appareil photo et vise <b>sa couleur</b> : sa page s’ouvre (sa carte, ${st.info?.kind === 'sablier' ? 'son sable, son STOP' : 'son pion, « Fait ! »'}). Sans compte, rien n’est gardé, le lien s’éteint à la fin de la partie.</p>
    ${cards}
    <div class="tel-tools two">${canTab ? `<button type="button" data-c="qr">${ICON.qr}Aussi sur l’écran de la partie</button>` : ''}<button type="button" data-qr-close>${IC.undo}Revenir à la télécommande</button></div>`;
}

function wireCoach(st: PhoneState, act: (kind: string, payload?: Record<string, unknown>) => Promise<void>, redraw: () => void): void {
  const el = document.querySelector<HTMLElement>('.tel')!;
  el.onclick = async e => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('button');
    if (!b || b.disabled) return;
    if (b.hasAttribute('data-take')) {
      try { const j = await phoneJoin(st.key, st.device, st.clock, true); if (j) { st.info = { ...st.info!, ...j }; st.me = j.me; } st.msg = null; } catch { st.msg = 'Pas maintenant (réseau) : réessaie.'; }
      redraw(); return;
    }
    if (b.dataset.open) { st.open = st.open === b.dataset.open ? null : b.dataset.open; st.armed = null; redraw(); return; }
    if (b.hasAttribute('data-qr-close')) { st.qr = false; redraw(); return; }
    if (b.hasAttribute('data-qr')) {
      st.qr = true; redraw();
      if ('live' in st.key) { try { st.tokens = await liveTokens(st.key.live); } catch { st.tokens = st.tokens ?? []; st.msg = 'Pas maintenant (réseau) : réessaie.'; } redraw(); }
      return;
    }
    const c = b.dataset.c;
    if (!c) return;
    // Le joker « annule un raté » du Parcours : on demande d'abord (comme le téléphone de l'équipe).
    const team = st.view?.teams.find(t => t.seat === b.dataset.seat);
    if (c === 'fail' && st.view?.kind === 'parcours' && team?.jokers.includes('cancel') && st.askSeat !== team.seat) { st.askSeat = team.seat; redraw(); return; }
    st.askSeat = null;
    // Les gestes lourds en deux touchers : une sanction, arrêter la partie.
    if (c === 'end' || c === 'sanction') {
      const key = c === 'end' ? 'end' : `${b.dataset.seat}:${b.dataset.v}`;
      if (st.armed !== key) { st.armed = key; redraw(); setTimeout(() => { if (st.armed === key) { st.armed = null; redraw(); } }, 4000); return; }
      st.armed = null;
    }
    const payload: Record<string, unknown> = {};
    if (b.dataset.seat) payload.seat = b.dataset.seat;
    if (b.dataset.v) payload.value = b.dataset.v;
    if (c === 'pause') payload.on = !st.view?.paused;
    if (c === 'shot') payload.made = b.dataset.made === '1';
    if (c === 'fail' && (b.dataset.joker === 'cancel' || (st.view?.kind === 'sablier' && team?.jokers.includes('cancel')))) payload.joker = 'cancel';
    if (c === 'qr') st.qr = false;
    await act(c, payload);
  };
}
